import {
  Badge,
  type ButtonVariant,
  buttonClass as designButtonClass,
  Notice,
  Button as UiButton,
} from '@mogzo/ui';
import { ChevronLeft } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';

// The dashboard's older helpers, now drawn by the design system (M25). New code uses
// @mogzo/ui directly.

export const cardClass = 'mz-panel';

const VARIANTS: Record<'primary' | 'brand' | 'secondary' | 'danger', ButtonVariant> = {
  primary: 'primary',
  brand: 'brand',
  secondary: 'secondary',
  danger: 'danger',
};

export function buttonClass(variant: keyof typeof VARIANTS = 'primary') {
  return designButtonClass(VARIANTS[variant]);
}

export function Button({
  variant = 'primary',
  className = '',
  ...button
}: { variant?: keyof typeof VARIANTS } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <UiButton variant={VARIANTS[variant]} className={className} {...button} />;
}

export const underlineLink =
  'font-medium text-ink underline decoration-brand decoration-2 underline-offset-4 hover:decoration-ink';

export function TextLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={underlineLink}>
      {children}
    </Link>
  );
}

const CHIP_TONES = {
  plain: 'neutral',
  ink: 'outline',
  brand: 'brand',
  danger: 'danger',
  mono: 'neutral',
} as const;

export function Chip({
  children,
  tone = 'plain',
}: {
  children: ReactNode;
  tone?: 'plain' | 'ink' | 'brand' | 'danger' | 'mono';
}) {
  return (
    <Badge tone={CHIP_TONES[tone]} className={tone === 'mono' ? 'mz-mono' : undefined}>
      {children}
    </Badge>
  );
}

export function Avatar({ text, size = 'md' }: { text: string; size?: 'md' | 'lg' }) {
  return (
    <span
      aria-hidden="true"
      className={`mz-avatar ${size === 'lg' ? 'size-12 text-lg' : 'size-8 text-xs'}`}
    >
      {text}
    </span>
  );
}

export function PageTitle({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="mz-pagehead-titles">
      <h1 className="mz-pagehead-title">{title}</h1>
      {subtitle && <p className="mz-pagehead-desc">{subtitle}</p>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <p role="status" className="py-10 text-center text-sm text-muted">
      {label}
    </p>
  );
}

export function ErrorNotice({ error }: { error: Error }) {
  return <Notice tone="danger">{error.message}</Notice>;
}

export const fieldClass =
  'block min-h-8 w-full rounded-[6px] border border-control bg-card px-2.5 py-1.5 text-ink placeholder:text-subtle focus:outline-2 focus:outline-offset-1 focus:outline-[var(--focus)]';

export function Alert({ tone, children }: { tone: 'error' | 'success'; children: ReactNode }) {
  return <Notice tone={tone === 'error' ? 'danger' : 'success'}>{children}</Notice>;
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex min-h-8 items-center gap-1 self-start text-sm font-medium text-muted hover:text-ink"
    >
      <ChevronLeft size={16} strokeWidth={1.75} aria-hidden="true" />
      {children}
    </Link>
  );
}
